import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Sparkles, ShieldCheck, Award, Droplets } from "lucide-react";

export default function SilverHero() {
  const [showcase, setShowcase] = useState([]);
  const [showIdx, setShowIdx] = useState(0);

  useEffect(() => {
    // Pull actual silver pieces the user wants highlighted: chains, payals, kadas
    axios.get(`${API}/products?category=silver&limit=24`).then(r => {
      const items = r.data?.items || [];
      const nameHits = (kw) => items.filter(p =>
        (p.name || "").toLowerCase().includes(kw) || (p.category || "").toLowerCase().includes(kw)
      );
      // Priority: chain, payal, kada, then any silver
      const priority = [
        ...nameHits("chain"),
        ...nameHits("payal"),
        ...nameHits("anklet"),
        ...nameHits("kada"),
        ...items,
      ];
      // dedupe by product_id, take first 4
      const seen = new Set();
      const picked = [];
      for (const it of priority) {
        if (!seen.has(it.product_id) && it.images?.[0]) {
          seen.add(it.product_id);
          picked.push(it);
        }
        if (picked.length >= 4) break;
      }
      setShowcase(picked);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (showcase.length < 2) return;
    const t = setInterval(() => setShowIdx(i => (i + 1) % showcase.length), 4200);
    return () => clearInterval(t);
  }, [showcase.length]);

  return (
    <section
      className="relative mb-10 rounded-3xl overflow-hidden bg-gradient-to-br from-[#1a1d24] via-[#2a2f38] to-[#0f1116]"
      data-testid="silver-hero"
    >
      {/* Subtle silver-noise texture */}
      <div
        className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(220,230,240,0.6) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(180,190,205,0.5) 0%, transparent 50%)",
        }}
      />
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22 viewBox=%220 0 120 120%22><g fill=%22none%22 stroke=%22%23c0c9d4%22 stroke-width=%220.3%22 stroke-opacity=%220.35%22><circle cx=%2260%22 cy=%2260%22 r=%2250%22/><circle cx=%2260%22 cy=%2260%22 r=%2235%22/><circle cx=%2260%22 cy=%2260%22 r=%2220%22/></g></svg>')",
      }} />

      <div className="relative grid md:grid-cols-[1.1fr_1fr] gap-0">
        {/* Left: story */}
        <div className="p-8 md:p-12 lg:p-14 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 mb-6">
            <Sparkles className="h-3.5 w-3.5 text-[#e8ebf0]" />
            <span className="text-[11px] tracking-widest uppercase text-[#e8ebf0] font-medium">
              The Silver Edit · 925 Sterling
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-white">
            Silver, told in <span className="italic text-[#c9d1dc]">whispers</span>.
          </h1>

          <p className="text-[#c9d1dc] text-base sm:text-lg leading-relaxed max-w-lg mb-8">
            Every Sumanya silver piece is cast in <span className="text-white font-semibold">92.5% pure sterling</span>,
            hand-finished by artisans in Rajasthan. From temple anklets to modern kada, this is silver with a story.
          </p>

          {/* Purity assurance strip (rate & hallmark intentionally removed per brand policy) */}
          <div className="inline-flex items-center gap-6 px-5 py-3 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md" data-testid="silver-hero-purity">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#8f97a3] mb-0.5">Purity</div>
              <div className="font-display text-2xl text-white">
                925<span className="text-sm text-[#8f97a3] font-body ml-1">Sterling</span>
              </div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#8f97a3] mb-0.5">Crafted in</div>
              <div className="font-display text-2xl text-white">India</div>
            </div>
          </div>
        </div>

        {/* Right: promise pillars + rotating product showcase */}
        <div className="relative p-8 md:p-12 lg:p-14 flex flex-col justify-center gap-5 bg-black/25 backdrop-blur-sm border-l border-white/5">
          {showcase.length > 0 && (
            <div className="mb-2" data-testid="silver-hero-showcase">
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                {showcase.map((p, i) => (
                  <img
                    key={p.product_id}
                    src={p.images?.[0]}
                    alt={p.name}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      i === showIdx ? "opacity-100" : "opacity-0"
                    }`}
                    data-testid={`silver-showcase-${i}`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                  <div className="text-[10px] tracking-widest uppercase text-[#c9d1dc]">Now trending</div>
                  <div className="text-white font-display text-lg leading-tight line-clamp-1">
                    {showcase[showIdx]?.name}
                  </div>
                </div>
                <div className="absolute top-3 right-3 flex gap-1.5">
                  {showcase.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1 rounded-full transition-all ${
                        i === showIdx ? "w-6 bg-white" : "w-1.5 bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          {[
            { Icon: ShieldCheck, t: "925 Certified", s: "Every piece stamped pure sterling silver — 92.5% fine." },
            { Icon: Droplets, t: "Anti-tarnish coat", s: "Rhodium finish keeps your silver bright for years." },
            { Icon: Award, t: "Lifetime Exchange", s: "Trade any silver piece back at 100% metal value, anytime." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-4 group">
              <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 group-hover:bg-white/10 group-hover:border-white/20 transition">
                <f.Icon className="h-5 w-5 text-[#e8ebf0]" strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-white font-semibold text-[15px] mb-0.5">{f.t}</div>
                <div className="text-[13px] text-[#a1a9b6] leading-relaxed">{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
